import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Modal, FormGroup } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { MultiSelect } from 'react-multi-select-component';
import { useTranslation } from 'react-i18next';
import { unionBy } from 'lodash';
import Select from 'react-select';
import {
  addBomTypeGroupAction,
  updateBomTypeGroupAction,
  fetchBomTypeGroupByIdAction,
  fetchBomTypeAction,
  getTier_1_ProductAssociatesAction,
} from '../Store/BomType.action';
import Loader from '../../../../Components/Utility/Loader/Loader';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';

/**
 * True when a BOM Type carries the given Product Type.
 * A group is scoped to one Product Type, so only BOM Types tagged with it may belong to it.
 * @param {object} bomType - BOM Type with a populated productTypes array.
 * @param {string} productTypeId - The group's Product Type id.
 * @returns {boolean}
 */
const hasProductType = (bomType, productTypeId) => bomType?.productTypes?.some((item) => item?._id === productTypeId);

function AddEditBomTypeGroup({
  onOpen,
  title,
  toggle,
  handleBomTypePopup,
  id,
  addBomTypeGroupAction,
  updateBomTypeGroupAction,
  fetchBomTypeGroupByIdAction,
  fetchBomTypeAction,
  bomIds,
  getTier_1_ProductAssociatesAction,
}) {
  const [productAssocList, setProductAssocList] = useState([]);
  const [productCopyAssocList, setCopyProductAssocList] = useState([]);

  const [productTypes, setProductTypeOptions] = useState([]);
  const [bomTypes, setBomTypes] = useState([]);
  const [showLoader, setShowLoader] = useState(true);
  const [producTypeVal, setBProductType] = useState(null);
  const { t } = useTranslation('common');
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    async function fetchData() {
      const pro_asso = await fetchBomTypeAction({ isAllRecord: true });
      const data = pro_asso.data.map((item) => {
        return { ...item, label: item.name, value: item._id };
      });
      let selectedBoms = [];
      if (bomIds && bomIds.length > 0) {
        selectedBoms = data.filter((item) => bomIds.includes(item.value));
      }
      data.sort((a, b) => a.label.localeCompare(b.label));
      setProductAssocList([...(data || [])]);
      setCopyProductAssocList([...(data || [])]);

      if (id) {
        const res = await fetchBomTypeGroupByIdAction(id);
        if (res.data) {
          const obj = {
            name: res.data.name,
            additionalInfo: res.data.additionalInfo,
            bomTypes: res.data.bomTypes.map((item) => {
              return {
                label: item.name,
                value: item._id,
              };
            }),
            productType: res.data.productType,
          };
          if (obj.productType) {
            let cloneBomType = [...data];
            cloneBomType = cloneBomType.filter((vl) => hasProductType(vl, obj?.productType?._id));
            const allowedBomTypeIds = new Set(cloneBomType.map((item) => item.value));
            const rejectedSelection = selectedBoms.filter((item) => !allowedBomTypeIds.has(item.value));
            if (selectedBoms.length && rejectedSelection.length === selectedBoms.length) {
              toasterMsg(`${t('BOM_TYPE_GROUP_VIEW.VALIDATION_ERROR.PRODUCT_TYPE_MISMATCH')} ${rejectedSelection.map((item) => item.label).join(', ')}`, 'danger');
              toggle();
              return;
            }
            if (!cloneBomType.length) {
              toasterMsg(`${t('BOM_TYPE_GROUP_VIEW.VALIDATION_ERROR.NO_BOM_TYPE_FOR_PRODUCT_TYPE')} ${obj?.productType?.name || ''}`.trim(), 'danger');
              toggle();
              return;
            }
            setProductAssocList([...(cloneBomType || [])]);
            const mergedBoms = unionBy(obj.bomTypes, selectedBoms, 'value');
            const rejectedBoms = mergedBoms.filter((item) => !allowedBomTypeIds.has(item.value));
            if (rejectedBoms.length) {
              toasterMsg(`${t('BOM_TYPE_GROUP_VIEW.VALIDATION_ERROR.PRODUCT_TYPE_MISMATCH')} ${rejectedBoms.map((item) => item.label).join(', ')}`, 'danger');
            }
            obj.bomTypes = mergedBoms.filter((item) => allowedBomTypeIds.has(item.value));
          } else {
            obj.bomTypes = unionBy(obj.bomTypes, selectedBoms, 'value');
          }
          for (const [key, value] of Object.entries(obj)) {
            setValue(key, value);
            if (key === 'bomTypes') {
              setBomTypes(value);
            }
            if (key === 'productType') {
              setBProductType(value);
            }
          }
        }
      } else {
        setValue('bomTypes', selectedBoms);

        setBomTypes(selectedBoms);
      }
      setShowLoader(false);
      let sortedproductAssocListData = [];

      const productType = await getTier_1_ProductAssociatesAction();
      if (productType?.data && productType?.code === 200) {
        const data = productType?.data?.map((item) => {
          return { ...item, label: item.name, value: item._id };
        });
        sortedproductAssocListData = data?.sort((a, b) => {
          return a?.label?.localeCompare(b.label);
        });
        setProductTypeOptions([...(sortedproductAssocListData || [])]);
      }
    }

    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitMethod = async (data) => {
    setShowLoader(true);
    const bomTypesIds = data.bomTypes.map((item) => item.value);
    data.name = data.name.trim();
    data.additionalInfo = data.additionalInfo.trim();
    const formData = { ...data, bomTypes: bomTypesIds, productType: producTypeVal?._id };
    let res;
    if (id) {
      formData._id = id;
      res = await updateBomTypeGroupAction(formData, t);
    } else {
      res = await addBomTypeGroupAction(formData, t);
    }
    if (res) {
      handleBomTypePopup(true);
    }
    setShowLoader(false);
  };

  const handleProductTypeChange = (val) => {
    setBProductType(val);
    let cloneBomType = [...productCopyAssocList];
    cloneBomType = cloneBomType.filter((vl) => hasProductType(vl, val._id));
    setValue('bomTypes', []);
    setProductAssocList(cloneBomType);
  };
  return (
    <Modal show={onOpen} size="md" backdrop="static" aria-labelledby="contained-modal-title-vcenter" className="custom-modal modal-small" onHide={toggle}>
      <form>
        <Modal.Header>
          <div className="d-flex justify-content-between align-items-center w-100">
            <Modal.Title id="contained-modal-title-vcenter">
              <span>{title}</span>
            </Modal.Title>
            <span onClick={toggle} className="icon-close" />
          </div>
        </Modal.Header>
        <Modal.Body>
          {showLoader && <Loader />}
          {!showLoader && (
            <>
              <FormGroup>
                <div className="input-group form-floating-label fixed-label">
                  <input
                    type="text"
                    name="name"
                    placeholder={t('BOM_TYPE_GROUP_VIEW.BOM_TYPE_GROUP_POPUP_VIEW.NAMES')}
                    {...register('name', {
                      required: true,
                      validate: (value) => !!value.trim() || 'no whitespaces.',
                    })}
                    className="form-control"
                  />
                  <label className="asterisk-req">
                    {t('BOM_TYPE_GROUP_VIEW.BOM_TYPE_GROUP_POPUP_VIEW.NAME')}
                    <span>*</span>
                  </label>
                </div>
                {errors.name && <small className="error-message">{t('BOM_TYPE_GROUP_VIEW.VALIDATION_ERROR.NAME_IS_REQUIRED')}</small>}
              </FormGroup>

              <div className="common-react-select">
                <label className="select-label asterisk-req">
                  {t('BOM_TYPE_VIEW.BOM_TYPE_POPUP_VIEW.PRODUCT_ASSOCIATION')}
                  <span>*</span>
                </label>
                <Select
                  name="productType"
                  {...register('productType', { required: true })}
                  options={productTypes}
                  getOptionLabel={(option) => `${option.name}`}
                  getOptionValue={(option) => `${option._id}`}
                  value={producTypeVal}
                  onChange={(val) => {
                    setValue('productType', val);
                    handleProductTypeChange(val);
                    setBomTypes([]);
                  }}
                  className="common-select"
                  classNamePrefix="common-select"
                />
                {errors.productType && <span className="error-message">{t('BOM_TYPE_VIEW.VALIDATION_ERROR.PRODUCT_TYPE_IS_REQUIRED')}</span>}
              </div>

              <FormGroup>
                <label className="select-label asterisk-req" style={{ fontFamily: 'unset', fontSize: '16px' }}>
                  {t('BOM_TYPE_GROUP_VIEW.BOM_TYPE_GROUP_POPUP_VIEW.BOM_TYPE')}
                  <span>*</span>
                </label>
                <MultiSelect
                  name="bomTypes"
                  {...register('bomTypes', { required: true })}
                  options={productAssocList}
                  className="custom-multiselect"
                  value={bomTypes}
                  onChange={(val) => {
                    setValue('bomTypes', val);
                    setBomTypes(val);
                  }}
                  labelledBy="Select BOM Type"
                  disabled={!producTypeVal}
                />
                {errors.bomTypes && bomTypes.length === 0 && (
                  <span className="error-message">{t('BOM_TYPE_GROUP_VIEW.VALIDATION_ERROR.BOM_TYPE_IS_REQUIRED')}</span>
                )}
              </FormGroup>

              <FormGroup>
                <div className="input-group form-floating-label fixed-label">
                  <input
                    type="text"
                    name="additionalInfo"
                    placeholder={t('BOM_TYPE_GROUP_VIEW.BOM_TYPE_GROUP_POPUP_VIEW.ADDITIONAL_INFO')}
                    {...register('additionalInfo', { required: false })}
                    className="form-control"
                  />
                  <label className="asterisk-req">{t('BOM_TYPE_GROUP_VIEW.BOM_TYPE_GROUP_POPUP_VIEW.ADDITIONAL_INFO')}</label>
                  {errors.additionalInfo && (
                    <small className="text-danger">{t('BOM_TYPE.BOM_TYPE_GROUP.ADD_EDIT_BOM_TYPE_GROUP.ADDITIONAL_INFO_REQUIRED')}</small>
                  )}
                </div>
              </FormGroup>

              <div className="modal-buttons mt-4 d-flex justify-content-between">
                <button type="button" onClick={toggle} className="btn btn-default">
                  {t('COMMON_LABEL.CANCEL')}{' '}
                </button>

                <button onClick={handleSubmit(onSubmitMethod)} className="btn btn-default">
                  {id ? t('COMMON_LABEL.UPDATE') : t('COMMON_LABEL.ADD')}
                </button>
              </div>
            </>
          )}
        </Modal.Body>
      </form>
    </Modal>
  );
}

const mapDispatchToProps = {
  updateBomTypeGroupAction,
  addBomTypeGroupAction,
  fetchBomTypeGroupByIdAction,
  fetchBomTypeAction,
  getTier_1_ProductAssociatesAction,
};

export default connect(null, mapDispatchToProps)(AddEditBomTypeGroup);
